import { QueryObserverOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { z } from 'zod';
import { useAccountParams } from '~/hooks/useAccountParams';
import { customFetch } from '~/utils/customFetch';

export const supplyManagerPartnerDataSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  logo: z.string().nullable(),
  domain: z.string().nullable(),
  isBlocked: z.boolean(),
  category: z.string(),
  version: z.number().optional(),
  language: z.string().nullable(),
  impressions: z.number(),
  networkImpressions: z.number(),
  actions: z.number(),
  productPrice: z.number().optional(),
  conversionRate: z.number(),
  cost: z.number(),
  cpa: z.number(),
  ctr: z.number(),
  clicks: z.number(),
  adjustment: z.number(),
  clientAdjustment: z.number(),
  productAdjustment: z.number(),
  clientRulesCount: z.number().optional(),
  productRulesCount: z.number().optional(),
  domainList: z.array(z.string()).nullable(),
  type: z.literal('publisher'),
});

export const supplyManagerPartnerGroupDataSchema = z.object({
  name: z.string(),
  logo: z.string().nullable(),
  host: z.string(),
  partners: z.array(supplyManagerPartnerDataSchema),
  impressions: z.number(),
  networkImpressions: z.number(),
  domainCount: z.number(),
  languageCount: z.number(),
  categoryCount: z.number(),
  partnerCount: z.number(),
  isBlocked: z.boolean(),
  type: z.literal('domain-group'),
});

export type SupplyManagerPartnerData = z.infer<typeof supplyManagerPartnerDataSchema>;
export type SupplyManagerPartnerGroupData = z.infer<typeof supplyManagerPartnerGroupDataSchema>;
export type RawSupplyManagerPartnersQueryData = {
  data: SupplyManagerPartnerGroupData[];
  metadata: { languages: string[]; partnerCategories: string[] };
};
export type SupplyManagerPartnersQueryData = {
  data: SupplyManagerPartnerGroupData[];
  languages: string[];
  categories: string[];
};
type Formats = 'png' | 'jpg';

type ImageSize = `${number}x${number}.${Formats}`;

function withImageSize(logo: string | null, imageSize: ImageSize): string | null {
  return logo
    ? `${process.env.REACT_APP_ENVIRONMENT !== 'prod' ? '/preview' : ''}${logo}/${imageSize}`
    : null;
}

export function supplyManagerPartnersQueryOptions({
  enabled,
  imageSize = '100x30.png',
  accountId,
  productId = undefined,
  from = dayjs().subtract(29, 'day').format('YYYYMMDD'),
  to = dayjs().format('YYYYMMDD'),
}: {
  enabled: boolean;
  imageSize?: ImageSize;
  accountId?: number;
  productId?: string;
  from?: string;
  to?: string;
}) {
  return {
    queryKey: ['supplyManagerPartners', accountId, enabled, productId, from, to, imageSize],
    queryFn: async (): Promise<SupplyManagerPartnersQueryData> => {
      const params: Record<string, string> = { v2: '1' };
      if (productId && productId !== 'all') {
        params.productId = productId;
      }
      if (from) {
        params.from = from;
      }
      if (to) {
        params.to = to;
      }

      const res = await customFetch<RawSupplyManagerPartnersQueryData>(
        `/authenticated/client/${accountId}/supply-manager`,
        {
          useNodeEndpoint: true,
          params,
        },
      );
      return {
        data: res.data.data.map((group) => ({
          ...group,
          logo: withImageSize(group.logo, imageSize),
          partners: group.partners.map((partner) => ({
            ...partner,
            logo: withImageSize(partner.logo, imageSize),
          })),
        })),
        languages: res.data.metadata.languages,
        categories: res.data.metadata.partnerCategories,
      };
    },
    enabled,
  } satisfies QueryObserverOptions;
}

export function useSupplyManagerPartnersQuery({
  imageSize = '100x30.png',
  productId,
  from,
  to,
  enabled = true,
}: {
  imageSize?: ImageSize;
  productId?: string;
  from?: string;
  to?: string;
  enabled?: boolean;
}) {
  const accountParams = useAccountParams();
  return useQuery(
    supplyManagerPartnersQueryOptions({
      imageSize,
      accountId: accountParams?.accountId,
      productId,
      from,
      to,
      enabled,
    }),
  );
}
